import * as React from 'react';
import Typesense from "typesense";

export function useOpenSearchBootstrap({ filters, baseFilters = [], host, index }) {
  const [loading, setLoading] = React.useState(true);
  const [initialFilters, setInitialFilters] = React.useState([]);
  const [allValues, setAllValues] = React.useState({});
  const cancelledRef = React.useRef(false);

  // Stable key for baseFilters so a new-but-equal array doesn't re-run the effect.
  const baseFiltersKey = JSON.stringify(baseFilters);

  React.useEffect(() => {
    if (!index || !filters || filters.length === 0) {
      setLoading(false);
      return;
    }

    cancelledRef.current = false;
    const controller = new AbortController();
    setLoading(true);

    const fields = filters.map(f => f.attribute);
    const aggs = {};
    fields.forEach(field => {
      aggs[field] = { terms: { field, size: 1000 } };
    });

    // baseFilters entries on fields that ALSO have a UI facet narrow the dropdown
    // options. Entries on non-facet fields are pure locked scopes (applied only
    // to hits at query time) and must not be used to restrict cascade aggregations,
    // otherwise unrelated fields collapse to the intersection.
    const facetFieldSet = new Set(fields);
    const facetBaseFilters = baseFilters.filter(bf => facetFieldSet.has(bf.field));

    async function run() {
      // Scope the initial fetch to facetBaseFilters so hard-excluded values
      // (admin whitelist on facet fields) never reach the facet UI as count-0
      // rows. Non-facet baseFilters are NOT applied here — those would shrink
      // unrelated field buckets to the intersection, not act as a whitelist.
      const initialData = await fetchOpenSearchAggregations({
        host, index, aggs, activeFilters: facetBaseFilters, signal: controller.signal
      });
      if (cancelledRef.current) return;

      const values = {};
      fields.forEach(field => {
        values[field] = (initialData.aggregations?.[field]?.buckets || []).map(b => String(b.key));
      });
      setAllValues(values);

      // Cascade through radio/dropdown filters sequentially — reuse initialData
      // since it's already scoped to facetBaseFilters.
      const selected = [];
      let currentData = initialData;

      for (const filter of filters) {
        const buckets = currentData.aggregations?.[filter.attribute]?.buckets || [];
        const available = buckets.map(b => String(b.key));
        if (available.length === 0) continue;

        let selectedValues = null;

        if (filter.kind === 'radio' || filter.kind === 'dropdown') {
          if (filter.defaultValues?.length > 0) {
            selectedValues = [filter.defaultValues.find(v => available.includes(v))].filter(Boolean);
          }
          if (!selectedValues?.length) {
            const sorted = [...available].sort((a, b) => a.localeCompare(b));
            selectedValues = [sorted[0]];
          }
        } else if (filter.kind === 'checkboxes') {
          if (filter.defaultValues?.length > 0) {
            const validDefaults = filter.defaultValues.filter(v => available.includes(v));
            if (validDefaults.length > 0) {
              selectedValues = validDefaults;
            }
          }
        }

        if (selectedValues?.length > 0) {
          selected.push({ field: filter.attribute, values: selectedValues, type: 'any' });

          currentData = await fetchOpenSearchAggregations({ host, index, aggs, activeFilters: [...facetBaseFilters, ...selected], signal: controller.signal });
          if (cancelledRef.current) return;
        }
      }

      setInitialFilters(selected);
    }

    run()
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        console.error('[NvChart] OpenSearch bootstrap failed:', err);
      })
      .finally(() => {
        if (!cancelledRef.current) setLoading(false);
      });

    return () => {
      cancelledRef.current = true;
      controller.abort();
    };
  }, [index, baseFiltersKey, filters, host]);

  return { loading, initialFilters, allValues };
}

async function fetchOpenSearchAggregations({ host, index, aggs, activeFilters, signal }) {
  const body = { size: 0, aggs };

  if (activeFilters.length > 0) {
    body.query = {
      bool: {
        filter: activeFilters.map(f => ({ terms: { [f.field]: f.values } })),
      },
    };
  }

  const headers = {
    'Content-Type': 'application/json',
    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
  };

  // Preview-mode pages render #library-preview-token with a signed token; without
  // it, OpenSearchController returns 403 for *_preview indexes.
  const previewToken = document.getElementById('library-preview-token')?.dataset?.value;
  if (previewToken) {
    headers['x-preview-token'] = previewToken;
  }

  const response = await fetch(`${host}/${index}/_search`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    throw new Error(`OpenSearch ${response.status} for ${index}`);
  }

  return response.json();
}

export function useFacetBootstrap(
  {
    adapter,
    facets,
    index,
    filters,
    filterBy,
    groupByFields,
  }) {
  const [loading, setLoading] = React.useState(true);
  const [initialUiState, setInitialUiState] = React.useState(null);
  const [facetCounts, setFacetCounts] = React.useState(null);
  const [finalFilterBy, setFinalFilterBy] = React.useState(null);

  const cancelledRef = React.useRef(false);

  React.useEffect(() => {
    if (!index || !filters || filters.length === 0) {
      setLoading(false);
      return;
    }

    cancelledRef.current = false;

    async function run() {
      setLoading(true);

      try {
        const result = await buildAutoFilterState({
          adapter,
          facets,
          index,
          filters,
          filterBy,
          groupByFields,
        });

        if (!result || cancelledRef.current) return;

        const baseState = {
          refinementList: {},
          menu: {},
        };

        for (const key of Object.keys(result.uiState)) {
          const filterConfig = filters.find(f => f.attribute === key);
          if (!filterConfig) continue;

          if (filterConfig.kind === 'radio' || filterConfig.kind === 'dropdown') {
            baseState.menu[key] = result.uiState[key][0];
          } else if (filterConfig.kind === 'checkboxes') {
            baseState.refinementList[key] = result.uiState[key];
          }
        }

        const indexState = {};
        if (Object.keys(baseState.refinementList).length) {
          indexState.refinementList = baseState.refinementList;
        }
        if (Object.keys(baseState.menu).length) {
          indexState.menu = baseState.menu;
        }

        setInitialUiState({[index]: indexState});
        setFacetCounts(result.facetCounts);
        setFinalFilterBy(result.finalFilterBy);
      } finally {
        if (!cancelledRef.current) {
          setLoading(false);
        }
      }
    }

    run();

    return () => {
      cancelledRef.current = true;
    };
  }, [
    adapter,
    facets,
    index,
    filters,
    filterBy,
    groupByFields,
  ]);

  return {
    loading,
    initialUiState,
    facetCounts,
    finalFilterBy,
  };
}

async function buildAutoFilterState({adapter, facets, index, filters, filterBy, groupByFields}) {
  const searchParameters = {
    q: "*",
    query_by: "*",
    facet_by: facets,
    max_facet_values: 50,
    filter_by: filterBy,
  };

  const serverConfig = adapter.configuration.server;

  const client = new Typesense.Client({
    nodes: [...serverConfig.nodes],
    apiKey: serverConfig.apiKey,
    connectionTimeoutSeconds: 2,
  });

  let uiState = {};
  let result = await client
    .collections(index)
    .documents()
    .search(searchParameters);

  if (!result) {
    return;
  }

  // Capture initial facet counts BEFORE applying any filters
  const initialFacetCounts = result.facet_counts;

  for (const filter of filters) {
    const facet = getFacet(result, filter.attribute);
    if (!facet || facet.counts.length === 0) {
      continue;
    }

    let selectedValues = null;

    if (filter.kind === 'radio' || filter.kind === 'dropdown') {
      if (filter.defaultValues && Array.isArray(filter.defaultValues) && filter.defaultValues.length > 0) {
        // Validate default exists in current facet counts
        const availableValues = facet.counts.map(c => c.value);
        const validDefault = filter.defaultValues.find(v => availableValues.includes(v));

        if (validDefault) {
          selectedValues = [validDefault];
        } else {
          // Fall back to first alphabetical if configured default is invalid
          const sorted = [...facet.counts].sort((a, b) => a.value.localeCompare(b.value));
          selectedValues = [sorted[0].value];
        }
      } else {
        // No default configured: auto-select first alphabetical value
        const sorted = [...facet.counts].sort((a, b) => a.value.localeCompare(b.value));
        selectedValues = [sorted[0].value];
      }
    } else if (filter.kind === 'checkboxes') {
      // Checkbox filters: Only select if defaultValues are configured
      if (filter.defaultValues && Array.isArray(filter.defaultValues) && filter.defaultValues.length > 0) {
        // Validate defaults exist in current facet counts
        const availableValues = facet.counts.map(c => c.value);
        const validDefaults = filter.defaultValues.filter(v => availableValues.includes(v));

        if (validDefaults.length > 0) {
          selectedValues = validDefaults;
        }
        // If no valid defaults, leave checkbox filter unselected
      }
      // If no defaultValues configured, leave checkbox filter unselected
    }

    // Only apply filter if we have selected values
    if (selectedValues && selectedValues.length > 0) {
      uiState[filter.attribute] = selectedValues;

      // Re-fetch facets with the new filter applied to cascade selections
      result = await client
        .collections(index)
        .documents()
        .search({
          q: "*",
          query_by: "*",
          facet_by: facets,
          max_facet_values: 50,
          filter_by: buildFilterBy(filterBy, uiState),
        });
    }
  }

  return {
    uiState,
    facetCounts: initialFacetCounts,  // Use initial facet counts, not final filtered ones
    lastResult: result,
    finalFilterBy: buildFilterBy(filterBy, uiState),
  };
}

function getFacet(result, attribute) {
  return result.facet_counts?.find(f => f.field_name === attribute);
}

function buildFilterBy(baseFilterBy, dynamicState) {
  const dynamicPart = Object.entries(dynamicState)
    .map(([attr, value]) => {
      if (Array.isArray(value)) {
        return `${attr}:=[${value.join(",")}]`;
      }
      return `${attr}:=${value}`;
    })
    .join(" && ");

  if (!baseFilterBy) return dynamicPart;
  if (!dynamicPart) return baseFilterBy;

  return `${baseFilterBy} && ${dynamicPart}`;
}